import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_UPLOAD_MS } from "@/lib/query-constants";
import type {
  BulkSetClientStartDateBody,
  BulkSetVendorMappingBody,
  BulkSetNewVendorMappingBody,
  CreativeImportPreviewDto,
  UpdateCreativeImportRowBody,
} from "@nexus/contracts";

function importQuery(tenantId: string): string {
  return `?tenantId=${encodeURIComponent(tenantId)}`;
}

export async function parseOrderImportSpreadsheet(
  tenantId: string,
  file: File,
): Promise<CreativeImportPreviewDto> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/parse${importQuery(tenantId)}`,
    {
      method: "POST",
      body: form,
      timeoutMs: API_REQUEST_TIMEOUT_UPLOAD_MS,
    },
  );
  return data;
}

export async function fetchOrderImportPreview(
  tenantId: string,
  importId: string,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}${importQuery(tenantId)}`,
  );
  return data;
}

export async function updateOrderImportRow(
  tenantId: string,
  importId: string,
  rowId: string,
  payload: UpdateCreativeImportRowBody,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/rows/${encodeURIComponent(rowId)}${importQuery(tenantId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function bulkSetOrderImportClientStartDates(
  tenantId: string,
  importId: string,
  payload: BulkSetClientStartDateBody,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/client-start-dates${importQuery(tenantId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function bulkSetOrderImportVendorMapping(
  tenantId: string,
  importId: string,
  payload: BulkSetVendorMappingBody,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/vendor-mapping${importQuery(tenantId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function bulkSetOrderImportNewVendorMapping(
  tenantId: string,
  importId: string,
  payload: BulkSetNewVendorMappingBody,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/new-vendor-mapping${importQuery(tenantId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function commitOrderImport(
  tenantId: string,
  importId: string,
): Promise<import("@nexus/contracts").CommitImportResultDto> {
  const { data } = await authFetch<import("@nexus/contracts").CommitImportResultDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/commit${importQuery(tenantId)}`,
    { method: "POST" },
  );
  return data;
}

export async function acceptPrefilledOrderImportDates(
  tenantId: string,
  importId: string,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/accept-prefilled-dates${importQuery(tenantId)}`,
    { method: "POST" },
  );
  return data;
}

export async function acceptOrderImportWarnings(
  tenantId: string,
  importId: string,
): Promise<CreativeImportPreviewDto> {
  const { data } = await authFetch<CreativeImportPreviewDto>(
    `/marketplace/admin/orders/import/${encodeURIComponent(importId)}/accept-warnings${importQuery(tenantId)}`,
    { method: "POST" },
  );
  return data;
}
